

import React, { useState, useEffect, useContext } from 'react'
import { Box, Typography, Button, Paper } from '@mui/material'
import { CommonContext } from '../contexts/CommonContext'
import { getCartSuggestions } from '../services/api'

const CartSuggestions = ({ cartData }) => {
  // State for suggestions
  const [suggestions, setSuggestions] = useState([])
  const [loadingSuggestions, setLoadingSuggestions] = useState(false)

  // Context functions
  const { updateCart, showSnackbar, isDesktop } = useContext(CommonContext)

  // Fetch suggestions when cart changes
  useEffect(() => {
    if (cartData && Object.keys(cartData).length > 0) {
      fetchCartSuggestions()
    } else {
      setSuggestions([])
    }
  }, [cartData])

  // Function to fetch suggestions from server API
  const fetchCartSuggestions = async () => {
    setLoadingSuggestions(true)
    
    try {
      const suggestionsResponse = await getCartSuggestions(cartData)
      setSuggestions(suggestionsResponse.data || [])
    } catch (error) {
      console.error("Error fetching suggestions:", error)
      setSuggestions([])
    } finally {
      setLoadingSuggestions(false)
    }
  }

  // Handle adding suggestion to cart
  const handleAddSuggestion = (product) => {
    updateCart(product, true)
    showSnackbar(`${product.name} added to cart!`, 'success')
  }

  // Get image source
  const getImageSrc = (product) => {
    if (product.imgUrls && product.imgUrls.length > 0) {
      return product.imgUrls[0]
    }
    if (product.imgUrl) {
      return product.imgUrl
    }
    return null
  }

  // Don't render anything if loading or no suggestions
  if (loadingSuggestions) {
    return (
      <Box sx={{ 
        padding: '20px', 
        textAlign: 'center',
        background: '#FFF0DB',
        margin: '20px 0',
        borderRadius: '8px'
      }}>
        <Typography>Loading suggestions...</Typography>
      </Box>
    )
  }

  if (suggestions.length === 0) {
    return null
  }

  // Main suggestions component
  return (
    <Box sx={{ margin: '20px 0' }}>
      {/* Header */}
      <Typography sx={{ 
        fontSize: '16px', 
        fontWeight: 'bold',
        marginBottom: '10px',
        color: '#2c2c2c',
        textAlign: 'center'
      }}>
        One tap = better breakfast tomorrow!
      </Typography>
      
      {/* Red container with products */}
      <Box sx={{ 
        background: '#a4243d',
        padding: '12px',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Horizontal scrollable carousel */}
        <Box sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: '12px',
          paddingBottom: '8px',
          '&::-webkit-scrollbar': {
            height: '6px',
            width: '0px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '10px',
            marginTop: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.6)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255,255,255,0.8)',
          }
        }}>
          {suggestions.map((product) => {
            const imageSrc = getImageSrc(product)
            
            return (
              <Box
                key={product.id}
                sx={{
                  minWidth: isDesktop ? '240px' : '170px',
                  maxWidth: isDesktop ? '240px' : '170px',
                  flexShrink: 0
                }}
              >
                <Paper sx={{
                  padding: '10px',
                  backgroundColor: '#FFF0DB',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: isDesktop ? '85px' : '75px'
                }}>
                  {/* Product image */}
                  <Box sx={{
                    width: isDesktop ? '65px' : '55px',
                    height: isDesktop ? '65px' : '55px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    backgroundColor: '#e0e0e0',
                    flexShrink: 0
                  }}>
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={product.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '6px'
                        }}
                      />
                    ) : (
                      <Typography sx={{ 
                        color: '#999', 
                        fontSize: '8px',
                        fontWeight: 'bold'
                      }}>
                        IMG
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Product info */}
                  <Box sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100%',
                    minHeight: isDesktop ? '65px' : '55px'
                  }}>
                    {/* Product name */}
                    <Typography sx={{ 
                      fontSize: isDesktop ? '12px' : '10px',
                      fontWeight: 'bold',
                      lineHeight: 1.1,
                      color: '#2c2c2c',
                      marginBottom: '2px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {product.name}
                    </Typography>
                    
                    {/* Product details */}
                    <Typography sx={{ 
                      fontSize: '8px', 
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      {product.qty || '6 Pc'}
                    </Typography>
                    
                    {/* Bottom row: Price and Add button */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'auto'
                    }}>
                      {/* Price */}
                      <Typography sx={{ 
                        fontSize: isDesktop ? '13px' : '11px',
                        fontWeight: 'bold',
                        color: '#2c2c2c'
                      }}>
                        ₹{product.price}
                      </Typography>
                      
                      {/* Add button */}
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          backgroundColor: '#a4243d',
                          color: 'white',
                          fontSize: '9px',
                          padding: '4px 12px',
                          height: '26px',
                          minWidth: '50px',
                          borderRadius: '5px',
                          '&:hover': {
                            backgroundColor: '#8b1e35'
                          }
                        }}
                        onClick={() => handleAddSuggestion(product)}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}

export default CartSuggestions